import React, { useContext, useState } from 'react';
import styled from 'styled-components';
import { useQuery } from '@tanstack/react-query';
import { apiGet } from 'helpers/ApiHelper';

import {
  ApplicationUIContext,
  ContentWrapper,
  SummaryGrid,
  useModalState,
} from '@yuhuinc/ui-library';
import Spacer from 'components/ui/Spacer';

import { Form, Input, Card, Space, Typography, Divider, Select, message, Button } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import AssociationsEditModal from './AssociationsEditModal';

import { getTranslatedString } from 'helpers/i18n';
import DocumentUpload from './DocumentUpload';

const { Option } = Select;
const { Title } = Typography;

const requiredField = {
  required: true,
  message: getTranslatedString('requiredField'),
};

const StyledSpace = styled(Space)`
  width: 100%;
`;

const StyledFormItem = styled(Form.Item)`
  margin-top: 12px;
`;

const StyledSectionContainer = styled.div`
  margin-bottom: 16px;
`;

const requiredAtLeastOneAssociation = {
  validator: async (_, unit_ids) => {
    if (!unit_ids || unit_ids.length < 1) {
      return Promise.reject(
        new Error(
          getTranslatedString('landlordPortal.applicationPackages.form.associations.errorMessage')
        )
      );
    }
  },
};

export default function Configuration({ formName, formData = {} }) {
  const form = Form.useFormInstance();

  const { isMobile } = useContext(ApplicationUIContext);
  const associationsEditModal = useModalState(false);

  const { data: templates } = useQuery({
    queryKey: ['templatesForPackages'],
    queryFn: () =>
      apiGet('/landlords/application_templates/templates', {
        application_status: 'published',
      }).then((response) => {
        return response?.application_templates;
      }),
    onError: () => message.error(getTranslatedString('unknownError')),
  });

  const { data: associations } = useQuery({
    queryKey: ['associations'],
    queryFn: () =>
      apiGet('/landlords/associations').then((response) => {
        return response?.sites;
      }),
    onError: () => message.error(getTranslatedString('unknownError')),
  });

  const setUnitsSelected = (selectedRows) => {
    const unitIds = selectedRows.map((r) => r.split('__').slice(-1)).flat();
    form.setFieldsValue({ unit_ids: unitIds });
  };

  return (
    <ContentWrapper>
      <Form.Item name="id" hidden>
        <Input />
      </Form.Item>

      <Form.Item name="status" hidden>
        <Input />
      </Form.Item>
      <Card style={{ maxWidth: 566 }} bodyStyle={{ padding: isMobile && 16 }}>
        <Title level={4}>
          {getTranslatedString('landlordPortal.applicationPackages.form.packageInformation')}
        </Title>

        <Divider />

        <StyledSpace direction="vertical" size="middle">
          <Form.Item
            name="name"
            label={getTranslatedString('landlordPortal.applicationPackages.form.name')}
            rules={[requiredField]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="description"
            label={getTranslatedString('landlordPortal.applicationPackages.form.description')}
            requiredMark={'optional'}
            tooltip={{ title: '', icon: <InfoCircleOutlined /> }}
          >
            <Input.TextArea rows={2} />
          </Form.Item>
        </StyledSpace>

        <Divider />

        <Title level={4}>
          {getTranslatedString('landlordPortal.applicationPackages.form.templatesDocuments')}
        </Title>
        <Title level={5}>
          {getTranslatedString('landlordPortal.applicationPackages.form.leadAppicant')}
        </Title>
        <StyledFormItem
          label={getTranslatedString('landlordPortal.applicationPackages.form.template')}
          name="lead_applicant_template_id"
        >
          <Select
            placeholder={getTranslatedString(
              'landlordPortal.applicationPackages.form.selectTemplate'
            )}
          >
            {(templates || []).map((option) => (
              <Option key={`lead-applicant-templates-${option.id}`} value={option.id}>
                {option.name}
              </Option>
            ))}
          </Select>
        </StyledFormItem>
        <DocumentUpload fieldName="lead_applicant_documents" />

        <Title level={5}>
          {getTranslatedString('landlordPortal.applicationPackages.form.coApplicant')}
        </Title>
        <StyledFormItem
          label={getTranslatedString('landlordPortal.applicationPackages.form.template')}
          name="coapplicant_template_id"
        >
          <Select
            placeholder={getTranslatedString(
              'landlordPortal.applicationPackages.form.selectTemplate'
            )}
          >
            {(templates || []).map((option) => (
              <Option key={`co-applicant-templates-${option.id}`} value={option.id}>
                {option.name}
              </Option>
            ))}
          </Select>
        </StyledFormItem>
        <DocumentUpload fieldName="coapplicant_documents" />

        <Title level={5}>
          {getTranslatedString('landlordPortal.applicationPackages.form.guarantor')}
        </Title>
        <StyledFormItem
          label={getTranslatedString('landlordPortal.applicationPackages.form.template')}
          name="guarantor_template_id"
        >
          <Select
            placeholder={getTranslatedString(
              'landlordPortal.applicationPackages.form.selectTemplate'
            )}
          >
            {(templates || []).map((option) => (
              <Option key={`guarantor-templates-${option.id}`} value={option.id}>
                {option.name}
              </Option>
            ))}
          </Select>
        </StyledFormItem>
        <DocumentUpload fieldName="guarantor_documents" />

        <Title level={5}>
          {getTranslatedString('landlordPortal.applicationPackages.form.landlordReference')}
        </Title>
        <StyledFormItem
          label={getTranslatedString('landlordPortal.applicationPackages.form.template')}
          name="landlord_reference_template_id"
        >
          <Select
            placeholder={getTranslatedString(
              'landlordPortal.applicationPackages.form.selectTemplate'
            )}
          >
            {(templates || []).map((option) => (
              <Option key={`landlord-reference-${option.id}`} value={option.id}>
                {option.name}
              </Option>
            ))}
          </Select>
        </StyledFormItem>
        <Title level={5}>
          {getTranslatedString('landlordPortal.applicationPackages.form.employerReference')}
        </Title>
        <StyledFormItem
          label={getTranslatedString('landlordPortal.applicationPackages.form.template')}
          name="employer_reference_template_id"
        >
          <Select
            placeholder={getTranslatedString(
              'landlordPortal.applicationPackages.form.selectTemplate'
            )}
          >
            {(templates || []).map((option) => (
              <Option key={`employer-reference-${option.id}`} value={option.id}>
                {option.name}
              </Option>
            ))}
          </Select>
        </StyledFormItem>
        <Title level={5}>
          {getTranslatedString('landlordPortal.applicationPackages.form.personalReference')}
        </Title>
        <StyledFormItem
          label={getTranslatedString('landlordPortal.applicationPackages.form.template')}
          name="personal_reference_template_id"
        >
          <Select
            placeholder={getTranslatedString(
              'landlordPortal.applicationPackages.form.selectTemplate'
            )}
          >
            {(templates || []).map((option) => (
              <Option key={`personal-reference-${option.id}`} value={option.id}>
                {option.name}
              </Option>
            ))}
          </Select>
        </StyledFormItem>

        <Divider />

        <Title level={4}>
          {getTranslatedString('landlordPortal.applicationPackages.form.depositsTile')}
        </Title>

        <StyledFormItem
          label={getTranslatedString('landlordPortal.applicationPackages.form.depositsLabel')}
          name="deposit_required"
          required
        >
          <Select>
            <Option key="deposit_required__application_submission" value="application_submission">
              {getTranslatedString('landlordPortal.applicationPackages.form.appSubmission')}
            </Option>
            <Option key="deposit_required__lease_creation" value="lease_creation">
              {getTranslatedString('landlordPortal.applicationPackages.form.leaseCreation')}
            </Option>
            <Option key="deposit_required__move_in" value="move_in">
              {getTranslatedString('landlordPortal.applicationPackages.form.movein')}
            </Option>
          </Select>
        </StyledFormItem>
        <Divider />

        <StyledSectionContainer>
          <Title level={5}>
            {getTranslatedString('landlordPortal.applicationPackages.form.associations')}
          </Title>

          <Form.List
            name="unit_ids"
            rules={[requiredAtLeastOneAssociation]}
            shouldUpdate={(prevValues, currentValues) =>
              prevValues.unit_ids !== currentValues.unit_ids
            }
          >
            {(fields, operations, { errors }) => {
              return (
                <>
                  <Form.ErrorList errors={errors} />

                  {fields.length > 0 && (
                    <SummaryGrid
                      values={[
                        [
                          getTranslatedString(
                            'landlordPortal.applicationPackages.form.associatedUnits'
                          ),
                          fields.length,
                        ],
                      ]}
                    />
                  )}

                  <Spacer mt={1}>
                    <Button onClick={associationsEditModal.open}>
                      {getTranslatedString(
                        'landlordPortal.applicationPackages.form.editAssociations'
                      )}
                    </Button>
                  </Spacer>

                  <AssociationsEditModal
                    visible={associationsEditModal.isVisible}
                    handleClose={associationsEditModal.close}
                    dataSource={(associations?.length && associations) || []}
                    onSelection={setUnitsSelected}
                    selectedRows={form.getFieldValue(['unit_ids'])?.map((id) => `unit__${id}`)}
                  />
                </>
              );
            }}
          </Form.List>
        </StyledSectionContainer>
      </Card>
    </ContentWrapper>
  );
}
